/**
 * Coverage Dashboard Page
 * Displays staff-to-child ratio coverage analysis for all groups
 */

import { useState, useMemo, useCallback } from 'react';
import { useLingui } from '@lingui/react';
import { Trans } from '@lingui/macro';
import { format, addDays, subDays, isToday as isDateToday } from 'date-fns';
import {
  AlertCircle,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  ChevronLeft,
  ChevronRight,
  Filter as FilterIcon,
  Building2 as Building2Icon,
  Users as UsersIcon,
  X as XIcon,
  FileDown,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { QueryErrorState } from '@/components/ui/query-error-state';
import { StatCard, StatGrid } from '@/components/ui/stat-card';
import { HelpTip } from '@/components/ui/help-tip';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { formatNumber } from '@/lib/format';
import { CoverageCard } from '@/components/shifts/CoverageCard';
import { AttendanceReportExportDialog } from '@/components/shifts/AttendanceReportExportDialog';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import { useCoverage } from '@/hooks/useShiftCoverage';
import { useChildcares } from '@/hooks/settings/useChildcare';
import { useGroups } from '@/hooks/useGroups';
import type { GroupCoverage } from '@/types/coverage';

export interface CoverageDashboardPageProps {
  selectedDate: Date;
  onDateChange: (date: Date) => void;
  selectedChildcareId: string | null;
  onChildcareChange: (id: string | null) => void;
  selectedGroupId: string | null;
  onGroupChange: (id: string | null) => void;
  /** Called when clicking a staff member — navigate to shift planning with employee */
  onNavigateToEmployee?: (employeeId: string) => void;
}

/**
 * Coverage Dashboard Page Component
 * Shows:
 * - Filter + date navigation toolbar (matching Dashboard)
 * - Summary stats (sufficient/warning/critical counts)
 * - Grid of coverage cards for each group
 */
export function CoverageDashboardPage({
  selectedDate,
  onDateChange,
  selectedChildcareId,
  onChildcareChange,
  selectedGroupId,
  onGroupChange,
  onNavigateToEmployee,
}: CoverageDashboardPageProps) {
  const { i18n } = useLingui();
  const isAdmin = useIsAdmin();
  const [isAttendanceReportOpen, setIsAttendanceReportOpen] = useState(false);
  const isSelectedToday = isDateToday(selectedDate);

  const handlePrevDay = () => onDateChange(subDays(selectedDate, 1));
  const handleNextDay = () => onDateChange(addDays(selectedDate, 1));
  const handleToday = () => onDateChange(new Date());
  const handleDatePick = (date: Date | undefined) => {
    if (date) onDateChange(date);
  };

  // Format date for API call
  const dateString = useMemo(
    () => format(selectedDate, 'yyyy-MM-dd'),
    [selectedDate]
  );

  // Filter state (popover open is local)
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const { data: childcaresData } = useChildcares({ limit: 100, offset: 0 });
  const { data: groupsData } = useGroups(100, 0);

  const childcares = useMemo(() => childcaresData?.data || [], [childcaresData?.data]);
  const allGroups = useMemo(() => groupsData || [], [groupsData]);

  const filteredGroupsForDropdown = useMemo(() => {
    if (!selectedChildcareId) return allGroups;
    return allGroups.filter((g) => g.childcare_id === selectedChildcareId);
  }, [allGroups, selectedChildcareId]);

  const selectedChildcareName = useMemo(() => {
    if (!selectedChildcareId) return null;
    return childcares.find((c) => c.id === selectedChildcareId)?.name || null;
  }, [childcares, selectedChildcareId]);

  const selectedGroupName = useMemo(() => {
    if (!selectedGroupId) return null;
    return allGroups.find((g) => g.id === selectedGroupId)?.name || null;
  }, [allGroups, selectedGroupId]);

  const hasActiveFilters = !!selectedChildcareId || !!selectedGroupId;

  const handleChildcareChange = useCallback((value: string) => {
    if (value === '__all__') {
      onChildcareChange(null);
      onGroupChange(null);
    } else {
      onChildcareChange(value);
      if (selectedGroupId) {
        const group = allGroups.find((g) => g.id === selectedGroupId);
        if (group?.childcare_id !== value) onGroupChange(null);
      }
    }
  }, [onChildcareChange, onGroupChange, selectedGroupId, allGroups]);

  const handleGroupChange = useCallback((value: string) => {
    onGroupChange(value === '__all__' ? null : value);
  }, [onGroupChange]);

  const handleClearAllFilters = useCallback(() => {
    onChildcareChange(null);
    onGroupChange(null);
  }, [onChildcareChange, onGroupChange]);

  // Fetch coverage data
  const { data: coverageData, isLoading, isError, error, refetch } = useCoverage(dateString);

  // Extract and filter groups from response
  const groups = useMemo<GroupCoverage[]>(() => {
    const allCoverageGroups = coverageData?.groups ?? [];
    if (!hasActiveFilters) return allCoverageGroups;

    const matchingGroupIds = new Set<string>();
    for (const g of allGroups) {
      if (selectedGroupId && g.id !== selectedGroupId) continue;
      if (selectedChildcareId && g.childcare_id !== selectedChildcareId) continue;
      if (g.id) matchingGroupIds.add(g.id);
    }

    return allCoverageGroups.filter((cg) => cg.group_id && matchingGroupIds.has(cg.group_id));
  }, [coverageData?.groups, hasActiveFilters, allGroups, selectedGroupId, selectedChildcareId]);

  // Calculate summary counts from filtered groups
  const summaryStats = useMemo(() => {
    if (!hasActiveFilters) {
      return {
        sufficient: coverageData?.sufficient_count ?? 0,
        warning: coverageData?.warning_count ?? 0,
        critical: coverageData?.critical_count ?? 0,
        total: groups.length,
      };
    }
    let sufficient = 0;
    let warning = 0;
    let critical = 0;
    for (const g of groups) {
      if (g.status === 'SUFFICIENT') sufficient++;
      else if (g.status === 'WARNING') warning++;
      else if (g.status === 'CRITICAL') critical++;
    }
    return { sufficient, warning, critical, total: groups.length };
  }, [coverageData, groups, hasActiveFilters]);

  return (
    <div className="flex flex-col">
      {/* Spacer — matches the action buttons row height in ShiftPlanningPage (h-10 + pb-4) */}
      <div className="pb-4"><div className="h-10" /></div>

      <div className="flex flex-col gap-4">
        {/* Filter + Date navigation bar */}
        <div className="flex items-center gap-2">
          {/* Filter popover */}
          <Popover open={isFilterOpen} onOpenChange={setIsFilterOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className={cn('h-10 w-10', hasActiveFilters && 'border-primary text-primary')}
                aria-label={i18n._({ id: 'coverage.filter', message: 'Filter' })}
              >
                <FilterIcon className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-72" align="start">
              <div className="space-y-4">
                <h4 className="font-medium text-sm">
                  {i18n._({ id: 'coverage.filter.title', message: 'Filter coverage' })}
                </h4>
                <div className="space-y-2">
                  <label className="text-sm text-muted-foreground flex items-center gap-2">
                    <Building2Icon className="h-4 w-4" />
                    {i18n._({ id: 'capacity.filter.childcare', message: 'Childcare' })}
                  </label>
                  <Select value={selectedChildcareId || '__all__'} onValueChange={handleChildcareChange}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder={i18n._({ id: 'capacity.filter.allChildcares', message: 'All childcares' })} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">
                        {i18n._({ id: 'capacity.filter.allChildcares', message: 'All childcares' })}
                      </SelectItem>
                      {childcares.map((cc) => (
                        /* A childcare row from the API always carries an id; the generated model marks it optional. */
                        <SelectItem key={cc.id} value={cc.id!}>
                          {cc.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm text-muted-foreground flex items-center gap-2">
                    <UsersIcon className="h-4 w-4" />
                    {i18n._({ id: 'capacity.filter.group', message: 'Group' })}
                  </label>
                  <Select value={selectedGroupId || '__all__'} onValueChange={handleGroupChange}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder={i18n._({ id: 'capacity.filter.allGroups', message: 'All groups' })} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">
                        {i18n._({ id: 'capacity.filter.allGroups', message: 'All groups' })}
                      </SelectItem>
                      {filteredGroupsForDropdown.map((g) => (
                        <SelectItem key={g.id} value={g.id || ''}>
                          {g.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </PopoverContent>
          </Popover>

          <Button
            variant="outline"
            size="icon"
            className="h-10 w-10"
            onClick={handlePrevDay}
            aria-label={i18n._({ id: 'common.previousDay', message: 'Previous day' })}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          <div className="w-full sm:w-[210px]">
            <div className="flex gap-2">
              <div className="flex-1 min-w-0">
                <DatePicker
                  value={selectedDate}
                  onChange={handleDatePick}
                  showClearButton={false}
                />
              </div>
              <Button
                variant="outline"
                className="shrink-0 h-10 px-3"
                onClick={handleToday}
                disabled={isSelectedToday}
              >
                <span className="text-xs font-medium">
                  {i18n._({ id: 'coverage.today', message: 'Today' })}
                </span>
              </Button>
            </div>
          </div>

          <Button
            variant="outline"
            size="icon"
            className="h-10 w-10"
            onClick={handleNextDay}
            aria-label={i18n._({ id: 'common.nextDay', message: 'Next day' })}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>

          {isAdmin && (
            <Button
              variant="outline"
              className="h-10"
              onClick={() => setIsAttendanceReportOpen(true)}
            >
              <FileDown className="h-4 w-4 mr-2" aria-hidden="true" />
              {i18n._({ id: 'coverage.attendanceReport.button', message: 'Inspection report' })}
            </Button>
          )}

          {/* What the numbers mean (#2038 UX pass) */}
          <HelpTip
            data-testid="coverage-help"
            label={i18n._({ id: 'coverage.help.label', message: 'How the cover is calculated' })}
            content={i18n._({
              id: 'coverage.help.text',
              message:
                'Cover compares the staff planned in the shift plan with the staff the children booked for this day need. The requirement comes from the staffing model in the capacity settings. Click a card to see the children or the staff of the group; a staff member opens the shift planner.',
            })}
          />

          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearAllFilters}
              className="text-muted-foreground hover:text-foreground"
            >
              <XIcon className="h-4 w-4 mr-1" aria-hidden="true" />
              <Trans id="coverage.filter.clearAll">Clear All Filters</Trans>
            </Button>
          )}
        </div>

        {/* Active filter badges */}
        {hasActiveFilters && (
          <div className="flex flex-wrap gap-2">
            {selectedChildcareName && (
              <div className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-primary/10 text-primary border border-primary/20 rounded-md">
                <Building2Icon className="h-3 w-3" aria-hidden="true" />
                <span className="truncate max-w-32">{selectedChildcareName}</span>
                <button
                  type="button"
                  onClick={() => { onChildcareChange(null); onGroupChange(null); }}
                  className="ml-1 hover:text-primary/70"
                  aria-label={i18n._({ id: 'shifts.filter.removeChildcare', message: 'Remove childcare filter' })}
                >
                  <XIcon className="h-3 w-3" aria-hidden="true" />
                </button>
              </div>
            )}
            {selectedGroupName && (
              <div className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-primary/10 text-primary border border-primary/20 rounded-md">
                <UsersIcon className="h-3 w-3" aria-hidden="true" />
                <span className="truncate max-w-32">{selectedGroupName}</span>
                <button
                  type="button"
                  onClick={() => onGroupChange(null)}
                  className="ml-1 hover:text-primary/70"
                  aria-label={i18n._({ id: 'shifts.filter.removeGroup', message: 'Remove group filter' })}
                >
                  <XIcon className="h-3 w-3" aria-hidden="true" />
                </button>
              </div>
            )}
          </div>
        )}

        {/* Summary Stats */}
        <StatGrid>
          <StatCard
            label={i18n._({ id: 'coverage.summary.sufficient', message: 'Sufficient' })}
            value={formatNumber(summaryStats.sufficient, i18n.locale)}
            icon={CheckCircle2}
            tone="success"
          />
          <StatCard
            label={i18n._({ id: 'coverage.summary.warning', message: 'Warning' })}
            value={formatNumber(summaryStats.warning, i18n.locale)}
            icon={AlertTriangle}
            tone="warning"
          />
          <StatCard
            label={i18n._({ id: 'coverage.summary.critical', message: 'Critical' })}
            value={formatNumber(summaryStats.critical, i18n.locale)}
            icon={XCircle}
            tone="danger"
          />
        </StatGrid>

      {/* Loading State */}
      {isLoading && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map((n) => (
            <Card key={n}>
              <CardHeader>
                <Skeleton className="h-6 w-32" />
              </CardHeader>
              <CardContent className="space-y-3">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-8 w-20" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Error State */}
      {isError && (
        <QueryErrorState
          error={error}
          onRetry={() => void refetch()}
          title={i18n._({ id: 'coverage.error.title', message: 'Error loading coverage data' })}
        />
      )}

      {/* Empty State */}
      {!isLoading && !isError && groups.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" aria-hidden="true" />
            <h3 className="text-lg font-medium">
              {i18n._({ id: 'coverage.empty.title', message: 'No coverage data' })}
            </h3>
            <p className="text-sm text-muted-foreground text-center mt-2">
              {i18n._({
                id: 'coverage.empty.description',
                message: 'No groups with active allocations found for this date.',
              })}
            </p>
          </CardContent>
        </Card>
      )}

        {/* Coverage Cards Grid */}
        {!isLoading && !isError && groups.length > 0 && (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {groups.map((group) => (
              <CoverageCard
                key={group.group_id}
                group={group}
                date={dateString}
                onStaffClick={onNavigateToEmployee}
              />
            ))}
          </div>
        )}
      </div>

      {isAdmin && (
        <AttendanceReportExportDialog
          open={isAttendanceReportOpen}
          onOpenChange={setIsAttendanceReportOpen}
          defaultChildcareId={selectedChildcareId}
        />
      )}
    </div>
  );
}
